import { htmlPieces, type HtmlAttr, type HtmlPiece } from "./simpleHtml";

/** The signature's short list of simple HTML (the signature HTML and role round, 2026-10-28; brief rule 2, "After the red team's review"):
 *  the signature is always read as simple HTML, and only these tags with these attributes survive, at every fill and at save. A tag that is
 *  not on the list goes with its words kept (a script's and a style's words go too); an address in angle brackets is text. */
export const SIGNATURE_TAGS = ["p", "br", "small", "strong", "b", "em", "i", "u", "a", "hr", "ul", "ol", "li", "table", "thead", "tbody", "tr", "td", "th"] as const;
export type SignatureTag = (typeof SIGNATURE_TAGS)[number];
const ALLOWED = new Set<string>(SIGNATURE_TAGS);
/** tags that never hold anything */
export const SIGNATURE_VOID = new Set(["br", "hr"]);
/** tags that lay a signature out by themselves: a line break next to one is only space (finding 4) */
export const SIGNATURE_BLOCKS = new Set(["p", "hr", "ul", "ol", "li", "table", "thead", "tbody", "tr", "td", "th"]);

/** a link's address when it is a full web or mail address (tabs, new lines and outer spaces ignored, as browsers do), else null */
export function safeHref(value: string): string | null {
  const v = value.replace(/[\t\n\r]/g, "").replace(/^[\u0000- ]+|[\u0000- ]+$/g, "");
  return /^(https?:\/\/[^\s/]|mailto:[^\s])/i.test(v) ? v : null;
}

/** the attributes a tag keeps: `href` (a full http, https or mailto address) and `title` on `a`, a number as `colspan` on `td` and `th` */
export function keptAttrs(tag: string, attrs: readonly HtmlAttr[]): HtmlAttr[] {
  const kept: HtmlAttr[] = [];
  for (const a of attrs) {
    if (kept.some((k) => k.name === a.name)) continue;
    if (tag === "a" && a.name === "href" && safeHref(a.value) !== null) kept.push({ ...a, value: safeHref(a.value)! });
    else if (tag === "a" && a.name === "title") kept.push(a);
    else if ((tag === "td" || tag === "th") && a.name === "colspan" && /^\s*\d{1,3}\s*$/.test(a.value)) kept.push({ ...a, value: a.value.trim() });
  }
  return kept;
}

export type SignaturePiece = { kind: "text"; raw: string } | { kind: "tag"; name: string; close: boolean; attrs: HtmlAttr[]; raw: string; changed: boolean };

/** the pieces of a signature that survive the short list, in order (comments, scripts and tags off the list gone) */
export function signaturePieces(template: string): SignaturePiece[] {
  const out: SignaturePiece[] = [];
  for (const p of htmlPieces(template)) {
    const kept = keep(p);
    if (kept) out.push(kept);
  }
  return out;
}

function keep(p: HtmlPiece): SignaturePiece | null {
  if (p.kind === "skip") return null;
  if (p.kind === "text") return p;
  if (!ALLOWED.has(p.name)) return null;
  if (p.close) return SIGNATURE_VOID.has(p.name) ? null : { ...p, changed: false };
  const attrs = keptAttrs(p.name, p.attrs);
  return { ...p, attrs, changed: attrs.length !== p.attrs.length };
}

const quoted = (a: HtmlAttr) => `${a.name}="${(a.quote === '"' ? a.raw : a.raw.replace(/"/g, "&quot;"))}"`;

/** The signature as it is stored (the save, on the server, for the workspace and every project): what the short list refuses is removed,
 *  everything it keeps stays exactly as typed ("R&D" stays "R&D", "Christian <christian@kriesi.at>" stays), so the editor shows the owner's
 *  own text after a reload. A kept tag that lost an attribute is written again with the attributes it keeps. */
export function cleanSignature(template: string): string {
  return signaturePieces(template).map((p) => {
    if (p.kind === "text" || !p.changed) return p.raw;
    return `<${p.name}${p.attrs.map((a) => ` ${quoted(a)}`).join("")}>`;
  }).join("");
}
